// Catalogo dei modelli per provider: un modello per riga con capacità, input
// supportati, funzioni, finestra di contesto, prezzo e stato del ciclo di vita.
// Fonti: GET /admin/models/catalog (una riga per modello e capacità, modelli
// nativi) e gli endpoint Custom AI del tenant (/custom-ai/endpoints/:id/models).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { CatalogRowDtoSchema, type CatalogRowDto, type ModelGovernance, GEMINI_EMBEDDING_GOVERNANCE, resolveGooglePriceSnapshot } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import AiGovernanceHistory from './AiGovernanceHistory';

type LifecycleStatus = 'active' | 'deprecating' | 'unavailable';

interface CustomEndpointDto { id: string; name: string }
interface CustomModelDto {
  model_name: string;
  display_name: string | null;
  context_window: number | null;
  capabilities: Partial<Record<string, boolean>>;
  /** Falso quando l'endpoint non elenca più il modello. */
  available: boolean;
}

export interface CatalogModel {
  key: string;
  name: string;
  modelId: string;
  capabilities: ReadonlyArray<string>;
  inputs: ReadonlyArray<string>;
  features: ReadonlyArray<string>;
  contextWindow: number | null;
  /** Explicit currency, per million tokens. */
  price: { input: number; output: number; currency: string } | null;
  status: LifecycleStatus;
  preview: boolean;
  recommendedFor: number;
  source?: string;
  revision?: number;
  governance?: ModelGovernance;
  listingConflict?: boolean;
}

type ProviderTab = 'openai' | 'claude' | 'google' | 'custom';
const PROVIDER_TABS: ReadonlyArray<{ id: ProviderTab; label: string }> = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'claude', label: 'Claude' },
  { id: 'google', label: 'Gemini' },
  { id: 'custom', label: 'Custom AI' },
];

const CAPABILITY_LABEL: Record<string, string> = {
  chat: 'Chat', embedding: 'Embedding', tts: 'Sintesi vocale', stt: 'Trascrizione', rerank: 'Rerank',
  vision: 'Visione', image: 'Immagini', multimodal: 'Multimodale', realtime: 'Realtime',
};
const INPUT_LABEL: Record<string, string> = { text: 'Testo', image: 'Immagini', audio: 'Audio', video: 'Video', pdf: 'PDF' };
const FEATURE_LABEL: Record<string, string> = {
  tool_calling: 'Strumenti', tools: 'Strumenti', function_calling: 'Strumenti', json_mode: 'JSON', structured_output: 'Output strutturato',
  vision: 'Visione', streaming: 'Streaming', reasoning: 'Ragionamento', image_generation: 'Genera immagini', prompt_caching: 'Cache del prompt',
};
const STATUS_META: Record<LifecycleStatus, { label: string; className: string }> = {
  active: { label: 'Attivo', className: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20' },
  deprecating: { label: 'In dismissione', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20' },
  unavailable: { label: 'Non disponibile', className: 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20' },
};
const STATUS_RANK: Record<LifecycleStatus, number> = { active: 0, deprecating: 1, unavailable: 2 };

const label = (map: Record<string, string>, v: string): string => map[v] ?? v.replace(/_/g, ' ');

function priceOf(pricing: Record<string, unknown> | null): CatalogModel['price'] {
  if (pricing?.unit !== 'per_1k_tokens' || typeof pricing.currency !== 'string') return null;
  const inP = pricing.input_per_1k;
  const outP = pricing.output_per_1k;
  if (typeof inP !== 'number' || typeof outP !== 'number' || !Number.isFinite(inP) || !Number.isFinite(outP)) return null;
  return { input: inP * 1000, output: outP * 1000, currency: pricing.currency };
}

/** Una riga per modello: unisce le righe del catalogo (una per capacità). */
export function groupCatalogRows(rows: ReadonlyArray<CatalogRowDto>): Map<string, CatalogModel[]> {
  const byProvider = new Map<string, Map<string, CatalogModel>>();
  for (const r of rows) {
    const models = byProvider.get(r.provider) ?? new Map<string, CatalogModel>();
    byProvider.set(r.provider, models);
    const status: LifecycleStatus = r.lifecycle_status ?? (r.deprecated ? 'deprecating' : 'active');
    const prev = models.get(r.model_id);
    const uniq = (a: ReadonlyArray<string>, b: ReadonlyArray<string>) => [...new Set([...a, ...b])];
    models.set(r.model_id, prev
      ? {
        ...prev,
        capabilities: uniq(prev.capabilities, [r.capability]),
        inputs: uniq(prev.inputs, r.modality ?? []),
        features: uniq(prev.features, r.features ?? []),
        contextWindow: Math.max(prev.contextWindow ?? 0, r.context_window ?? 0) || null,
        price: prev.price ?? priceOf(r.pricing),
        status: STATUS_RANK[status] > STATUS_RANK[prev.status] ? status : prev.status,
        preview: prev.preview || r.preview,
        recommendedFor: prev.recommendedFor + (r.is_recommended_for?.length ?? 0),
      }
      : {
        key: `${r.provider}::${r.model_id}`,
        source: r.source,
        revision: r.governance_revision,
        governance: r.metadata?.governance,
        listingConflict: r.metadata?.latest_listing?.context_window != null
          && r.metadata.latest_listing.context_window !== r.context_window,
        name: r.display_name || r.model_id,
        modelId: r.model_id,
        capabilities: [r.capability],
        inputs: r.modality ?? [],
        features: r.features ?? [],
        contextWindow: r.context_window,
        price: priceOf(r.pricing),
        status,
        preview: r.preview,
        recommendedFor: r.is_recommended_for?.length ?? 0,
      });
  }
  const out = new Map<string, CatalogModel[]>();
  for (const [p, m] of byProvider) out.set(p, [...m.values()].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.name.localeCompare(b.name)));
  return out;
}

const CUSTOM_CAPS: ReadonlyArray<string> = ['chat', 'embedding', 'audio_transcription', 'audio_speech', 'rerank'];
const CUSTOM_CAP_LABEL: Record<string, string> = { audio_transcription: 'stt', audio_speech: 'tts' };
const CUSTOM_FEATURES: ReadonlyArray<string> = ['tools', 'json_mode', 'vision', 'streaming'];

function customModel(ep: CustomEndpointDto, m: CustomModelDto): CatalogModel {
  const caps = m.capabilities ?? {};
  return {
    key: `custom::${ep.id}::${m.model_name}`,
    name: m.display_name || m.model_name,
    modelId: m.model_name,
    capabilities: CUSTOM_CAPS.filter((c) => caps[c]).map((c) => CUSTOM_CAP_LABEL[c] ?? c),
    inputs: ['text', ...(caps.vision ? ['image'] : []), ...(caps.audio_transcription ? ['audio'] : [])],
    features: CUSTOM_FEATURES.filter((f) => caps[f]),
    contextWindow: m.context_window,
    price: null,
    status: m.available ? 'active' : 'unavailable',
    preview: false,
    recommendedFor: 0,
    source: ep.name,
  };
}

function formatContext(n: number | null): string | null {
  if (!n) return null;
  return n >= 1_000_000 ? `${(n / 1_000_000).toLocaleString('it-IT', { maximumFractionDigits: 1 })}M token` : `${Math.round(n / 1000)}k token`;
}

function formatPrice(p: NonNullable<CatalogModel['price']>): string {
  const f = (v: number) => v.toLocaleString('it-IT', { maximumFractionDigits: v < 1 ? 3 : 2 });
  return `${p.currency} ${f(p.input)} in · ${f(p.output)} out / 1M token`;
}

function GovernedDetails({ model }: { model: CatalogModel }) {
  const facts = model.governance ?? (model.modelId === 'gemini-embedding-2' ? GEMINI_EMBEDDING_GOVERNANCE : undefined);
  const google = model.key.startsWith('google::');
  const price = google ? resolveGooglePriceSnapshot(model.modelId, 0, 'standard') : null;
  const longContextPrice = google ? resolveGooglePriceSnapshot(model.modelId, 200_001, 'standard') : null;
  return <details className="mt-1 text-xs text-muted-foreground">
    <summary>Fonte: {model.source ?? 'non verificata'} · revisione {model.revision ?? 0}</summary>
    {!facts && <p>Specifiche governate non disponibili; il listing non certifica le capacità dedotte.</p>}
    {model.listingConflict && <p className="text-amber-700">Conflitto tra listing e specifiche conservate: il refresh non ha cambiato automaticamente la configurazione.</p>}
    {facts && Object.entries(facts).map(([key, fact]) => typeof fact === 'number' ? null :
      <p key={key}>{key}: {String(fact.value)} {fact.unit} · {fact.scope} · {fact.source} · {fact.status} · verifica {fact.verifiedAt ?? 'assente'}</p>)}
    {price && <p>Listino ufficiale {price.currency}/1M token, standard ({price.contextTier}): testo {price.input.TEXT}, immagini {price.input.IMAGE}, audio {price.input.AUDIO}, video {price.input.VIDEO}, output/thinking {price.outputTextIncludingThoughts ?? 'non applicabile'}. Fonte: {price.evidenceUrl} · verifica {price.verifiedAt} · revisione {price.revision}. Non è una fattura.</p>}
    {longContextPrice && price && longContextPrice.contextTier !== price.contextTier && <p>
      Contesto oltre 200k token ({longContextPrice.contextTier}): {longContextPrice.currency}/1M token · testo {longContextPrice.input.TEXT},
      immagini {longContextPrice.input.IMAGE}, audio {longContextPrice.input.AUDIO}, video {longContextPrice.input.VIDEO},
      output/thinking {longContextPrice.outputTextIncludingThoughts}. Fonte e verifica: {longContextPrice.evidenceUrl} · {longContextPrice.verifiedAt}.
    </p>}
    {google && !price && <p>Listino Google verificato non disponibile per questo ID.</p>}
  </details>;
}

export default function ModelCatalogByProvider() {
  const { user } = useAuth();
  return <ScopedModelCatalog key={`${user?.tenantId ?? ''}:${user?.id ?? ''}`} />;
}

function ScopedModelCatalog() {
  const [tab, setTab] = useState<ProviderTab>('openai');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [native, setNative] = useState<Map<string, CatalogModel[]>>(new Map());
  const [custom, setCustom] = useState<CatalogModel[]>([]);

  const load = useCallback(async (retired: boolean) => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await apiClient.get<{ rows?: CatalogRowDto[] }>(`/admin/models/catalog${retired ? '?include_deprecated=true' : ''}`);
      setNative(groupCatalogRows(CatalogRowDtoSchema.array().parse(res.data?.rows ?? [])));
    } catch {
      setNative(new Map());
      setLoadError('Catalogo non disponibile o contratto metadati non valido. Riprova il caricamento.');
    }
    try {
      const eps = (await apiClient.get<{ endpoints?: CustomEndpointDto[] }>('/custom-ai/endpoints')).data?.endpoints ?? [];
      const lists = await Promise.all(eps.map(async (ep) => {
        const r = await apiClient.get<{ models?: CustomModelDto[] }>(`/custom-ai/endpoints/${encodeURIComponent(ep.id)}/models`).catch(() => null);
        return (r?.data?.models ?? []).filter((m) => retired || m.available).map((m) => customModel(ep, m));
      }));
      setCustom(lists.flat());
    } catch {
      setCustom([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(includeRetired); }, [load, includeRetired]);

  const counts = useMemo<Record<ProviderTab, number>>(() => ({
    openai: native.get('openai')?.length ?? 0,
    claude: native.get('claude')?.length ?? 0,
    google: native.get('google')?.length ?? 0,
    custom: custom.length,
  }), [native, custom]);

  const models = useMemo(() => {
    const list = tab === 'custom' ? custom : (native.get(tab) ?? []);
    const q = query.trim().toLowerCase();
    return q ? list.filter((m) => m.name.toLowerCase().includes(q) || m.modelId.toLowerCase().includes(q)) : list;
  }, [tab, custom, native, query]);

  return (
    <div className="space-y-3" data-testid="model-catalog-by-provider">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex flex-wrap rounded-lg border bg-muted/40 p-0.5" role="tablist" aria-label="Provider">
          {PROVIDER_TABS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={tab === p.id}
              onClick={() => setTab(p.id)}
              data-testid={`catalog-tab-${p.id}`}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                tab === p.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {p.label} <span className="tabular-nums text-muted-foreground">{counts[p.id]}</span>
            </button>
          ))}
        </div>
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca un modello" className="h-8 pl-7 text-xs" aria-label="Cerca un modello" />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={includeRetired} onCheckedChange={setIncludeRetired} aria-label="Mostra anche i modelli in dismissione o non disponibili" />
          Mostra anche in dismissione o non disponibili
        </label>
      </div>

      {loadError && <p role="alert" className="text-sm text-destructive">{loadError}</p>}
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Caricamento del catalogo…</div>
      ) : models.length === 0 ? (
        <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
          {tab === 'custom' ? 'Nessun modello Custom AI: aggiungi un endpoint con "Aggiungi Custom AI".' : 'Nessun modello trovato.'}
        </p>
      ) : (
        <ul className="divide-y rounded-lg border bg-card" data-testid={`catalog-list-${tab}`}>
          {models.map((m) => {
            const status = STATUS_META[m.status];
            const ctx = formatContext(m.contextWindow);
            return (
              <li key={m.key} className="flex flex-col gap-1.5 p-3" data-testid={`catalog-model-${m.modelId}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{m.name}</span>
                  {m.name !== m.modelId && <code className="text-[10px] text-muted-foreground">{m.modelId}</code>}
                  {m.source && <Badge variant="outline" className="text-[10px]">{m.source}</Badge>}
                  <span className={`ml-auto inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${status.className}`}>{status.label}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {m.capabilities.map((c) => <Badge key={`c-${c}`} className="text-[10px] bg-primary/10 text-primary border-primary/20">{label(CAPABILITY_LABEL, c)}</Badge>)}
                  {m.inputs.map((i) => <Badge key={`i-${i}`} variant="outline" className="text-[10px]">{label(INPUT_LABEL, i)}</Badge>)}
                  {m.features.map((f) => <Badge key={`f-${f}`} variant="secondary" className="text-[10px]">{label(FEATURE_LABEL, f)}</Badge>)}
                  {m.preview && <Badge variant="outline" className="text-[10px]">Anteprima</Badge>}
                </div>
                {(ctx || m.price || m.recommendedFor > 0) && (
                  <p className="text-[11px] text-muted-foreground">
                    {[ctx && `Contesto ${ctx}`, m.price && formatPrice(m.price), m.recommendedFor > 0 && `Consigliato per ${m.recommendedFor} ${m.recommendedFor === 1 ? 'funzione' : 'funzioni'}`].filter(Boolean).join(' · ')}
                  </p>
                )}
                <GovernedDetails model={m} />
              </li>
            );
          })}
        </ul>
      )}
      <AiGovernanceHistory global />
    </div>
  );
}
